/**
 * PERSONAL ACTIVITY LOGGER (client SDK)
 *
 * Thin wrapper around POST /api/admin/personal-activity/log used to capture
 * tab navigation, search queries, and high-value admin actions for the
 * Personal Activity Report.
 *
 * Gated server-side by admin_users.personal_activity_report_enabled. The
 * client also caches the enabled flag so disabled admins make zero requests.
 *
 * NOT a financial audit ledger; calls to this module never affect
 * admin_audit_logs / financial_audit_log / kyc_audit_log / audit_log.
 */

import { getSessionHeaders } from './serverSession'

export type PersonalActivityCategory =
  | 'navigation'
  | 'search'
  | 'kyc'
  | 'payment'
  | 'user_edit'
  | 'commission'
  | 'withdrawal'
  | 'system'
  | 'export'
  | 'other'

export interface LogActivityInput {
  event_category: PersonalActivityCategory
  event_action: string
  target_type?: string | null
  target_id?: string | null
  target_label?: string | null
  search_query?: string | null
  search_surface?: string | null
  route_path?: string | null
  tab_id?: string | null
  details?: Record<string, unknown>
}

const ENABLED_CACHE_KEY = 'aureus.personalActivity.enabled.v1'
const ENABLED_CACHE_TTL_MS = 5 * 60 * 1000

interface EnabledCache { value: boolean; ts: number }

let enabledInflight: Promise<boolean> | null = null

function readCache(): EnabledCache | null {
  try {
    const raw = sessionStorage.getItem(ENABLED_CACHE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as EnabledCache
    if (!parsed || typeof parsed.value !== 'boolean' || typeof parsed.ts !== 'number') return null
    if (Date.now() - parsed.ts > ENABLED_CACHE_TTL_MS) return null
    return parsed
  } catch {
    return null
  }
}

function writeCache(value: boolean): void {
  try {
    sessionStorage.setItem(ENABLED_CACHE_KEY, JSON.stringify({ value, ts: Date.now() }))
  } catch {
    /* sessionStorage may be unavailable — fail silent */
  }
}

export function clearPersonalActivityEnabledCache(): void {
  enabledInflight = null
  try { sessionStorage.removeItem(ENABLED_CACHE_KEY) } catch { /* noop */ }
}

async function fetchEnabledFlag(): Promise<boolean> {
  try {
    const res = await fetch('/api/admin/me/personal-activity-enabled', {
      method: 'GET',
      headers: { ...getSessionHeaders() }
    })
    if (!res.ok) {
      writeCache(false)
      return false
    }
    const json = await res.json().catch(() => ({}))
    const enabled = Boolean(json?.enabled)
    writeCache(enabled)
    return enabled
  } catch {
    writeCache(false)
    return false
  }
}

/**
 * Returns whether the calling admin has the personal activity feature enabled.
 * Result is cached for ENABLED_CACHE_TTL_MS to avoid spamming the endpoint.
 * Concurrent callers share one request.
 */
export async function isPersonalActivityEnabled(force = false): Promise<boolean> {
  if (!force) {
    const cached = readCache()
    if (cached) return cached.value
    if (enabledInflight) return enabledInflight
  }

  const request = fetchEnabledFlag()
  enabledInflight = request
  try {
    return await request
  } finally {
    if (enabledInflight === request) enabledInflight = null
  }
}

/**
 * Fire-and-forget personal activity log call. Never throws. Returns false on
 * any failure (disabled, network error, server error). Disabled admins skip
 * the network round-trip entirely.
 */
export async function logActivity(input: LogActivityInput): Promise<boolean> {
  if (!input?.event_category || !input?.event_action) return false

  // Ask once, then skip the log call entirely when this admin is disabled.
  // A disabled log used to answer 403, which the browser prints as a failed resource.
  const enabled = await isPersonalActivityEnabled()
  if (!enabled) return false

  try {
    const res = await fetch('/api/admin/personal-activity/log', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
      body: JSON.stringify(input)
    })
    const json = await res.json().catch(() => ({}))
    if (res.status === 403 || json?.code === 'PERSONAL_ACTIVITY_DISABLED') {
      writeCache(false)
      return false
    }
    return res.ok && json?.success !== false
  } catch {
    return false
  }
}

/**
 * Convenience helpers — keep call sites short and consistent.
 */
export const logNavigation = (tab_id: string, target_label?: string, route_path?: string) =>
  logActivity({ event_category: 'navigation', event_action: 'tab_change', tab_id, target_label, route_path })

export const logSearch = (
  search_surface: string,
  search_query: string,
  details?: Record<string, unknown>
) =>
  logActivity({
    event_category: 'search',
    event_action: 'search_submit',
    search_surface,
    search_query,
    details
  })

export const logAction = (
  category: PersonalActivityCategory,
  action: string,
  extra?: Partial<LogActivityInput>
) => logActivity({ event_category: category, event_action: action, ...(extra || {}) })
